/**
 * Etiquetas como chips, con autocompletado de las que la marca ya tiene (SPEC-BD §6.2 paso 3 y la ficha).
 *
 * Enter o coma agregan; la «×» de un chip lo saca. La regla de qué entra (≤ 40 caracteres, sin repetir
 * sin importar mayúsculas ni tildes, hasta el tope) es `agregarEtiquetas` de `logic/base.ts`, la misma
 * que aplica el backend. Si se elige una sugerencia se guarda con el texto del catálogo, así «feria 2026»
 * no aparece como otra forma de «Feria 2026».
 */

import { useId, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import { X } from "lucide-react";
import { MAX_ETIQUETAS_CARGA, agregarEtiquetas, normalizarTexto } from "../../logic/base";

interface Props {
  valores: string[];
  onChange: (valores: string[]) => void;
  sugerencias?: { clave: string; texto: string }[];
  max?: number;
  label?: string;
  ayuda?: string;
  disabled?: boolean;
}

export function InputEtiquetas({ valores, onChange, sugerencias = [], max = MAX_ETIQUETAS_CARGA, label, ayuda, disabled = false }: Props) {
  const [texto, setTexto] = useState("");
  const [error, setError] = useState("");
  const idInput = useId();
  const idLista = useId();

  const usadas = useMemo(() => new Set(valores.map(normalizarTexto)), [valores]);
  const opciones = useMemo(() => {
    const q = normalizarTexto(texto);
    if (!q) return [];
    return sugerencias.filter((s) => !usadas.has(normalizarTexto(s.texto)) && normalizarTexto(s.texto).includes(q)).slice(0, 8);
  }, [sugerencias, texto, usadas]);

  const agregar = (entrada: string) => {
    // Si coincide con una del catálogo, se usa su texto tal cual.
    const partes = entrada.split(/[,;|]/).map((p) => {
      const n = normalizarTexto(p);
      return sugerencias.find((s) => normalizarTexto(s.texto) === n)?.texto ?? p;
    });
    const r = agregarEtiquetas(valores, partes.join(","), max);
    setError(r.error);
    if (r.etiquetas.length !== valores.length) onChange(r.etiquetas);
    setTexto("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (texto.trim()) agregar(texto);
    } else if (e.key === "Backspace" && !texto && valores.length && !disabled) {
      onChange(valores.slice(0, -1));
    }
  };

  const lleno = valores.length >= max;

  return (
    <div>
      {label && <label htmlFor={idInput} className="text-xs font-semibold text-slate-600">{label}</label>}
      <div className={`mt-1.5 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 px-2 py-1.5 bg-white focus-within:ring-2 focus-within:ring-violet-500 ${disabled ? "bg-slate-50" : ""}`}>
        {valores.map((v) => (
          <span key={normalizarTexto(v)} className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold pl-2.5 pr-1 py-0.5">
            {v}
            <button type="button" disabled={disabled} onClick={() => onChange(valores.filter((x) => x !== v))} aria-label={`Sacar la etiqueta ${v}`}
              className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-violet-100 disabled:opacity-50">
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          id={idInput}
          value={texto}
          disabled={disabled || lleno}
          onChange={(e) => { setTexto(e.target.value.slice(0, 200)); setError(""); }}
          onKeyDown={onKey}
          onBlur={() => { if (texto.trim()) agregar(texto); }}
          placeholder={lleno ? `Hasta ${max} etiquetas` : valores.length ? "" : "Escribí y apretá Enter"}
          aria-describedby={ayuda ? `${idInput}-ayuda` : undefined}
          aria-controls={opciones.length ? idLista : undefined}
          className="flex-1 min-w-[8rem] h-7 text-sm focus:outline-none bg-transparent disabled:cursor-not-allowed"
        />
      </div>
      {opciones.length > 0 && (
        <ul id={idLista} role="listbox" aria-label="Etiquetas que ya existen" className="mt-1 rounded-xl border border-slate-100 bg-white shadow-sm py-1 max-h-48 overflow-auto">
          {opciones.map((o) => (
            <li key={o.clave} role="option" aria-selected="false">
              {/* onMouseDown: el blur del input agregaría el texto a medio escribir antes del click. */}
              <button type="button" onMouseDown={(e) => { e.preventDefault(); agregar(o.texto); }}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                {o.texto}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {ayuda && <p id={`${idInput}-ayuda`} className="text-[11px] text-slate-400 mt-1">{ayuda}</p>}
    </div>
  );
}

export default InputEtiquetas;
